import React from 'react';
import ColumnDefinition from '../../../infrastructure/types/DataGrid/ColumnDefinition';
import FieldDefinition from '../../../types/common/FieldDefinition';
import CustomIcon from '../../Common/BootstrapIcons/Custom/CustomIcon';
import iconTooltip from '../../Common/BootstrapIcons/Tooltip/IconTooltip';
import { ReactComponent as CopyIcon } from '../../Common/BootstrapIcons/copy.svg';
import { ReactComponent as RemoveIcon } from '../../Common/BootstrapIcons/close.svg';
import SelectOption from '../../../infrastructure/types/SelectOption';
import SelectList from '../../Common/SelectList/SelectList';
import ComponentType from '../../../types/report/ComponentType';
import ComponentChangeLogIconAndAction from '../../Common/Modals/ComponentChangeLogModal/ComponentChangeLogIconAndAction';
import DataSourceField from '../../../types/report/DataSourceField';
import ReportVariable from '../../../types/report/ReportVariable';
import mixedOperandHelper from '../../../infrastructure/helpers/functions/operand/mixedOperandHelper';

function getId(
    field: DataSourceField,
    fieldDefinitions: FieldDefinition[],
    variables: ReportVariable[]
): number {

    if (field.fieldId) {
        return fieldDefinitions.find(f => f.id === field.fieldId)?.id as number;
    } else if (field.variableNumber) {
        return variables.find(v => v.number === field.variableNumber)?.number as number;
    }

    return 0;
}

function getName(
    field: DataSourceField,
    fieldDefinitions: FieldDefinition[],
    variables: ReportVariable[]
): string {

    if (field.fieldId) {
        return fieldDefinitions.find(f => f.id === field.fieldId)?.name || '';
    } else if (field.variableNumber) {
        return variables.find(v => v.number === field.variableNumber)?.name || '';
    }

    return '';
}

function getFullName(
    field: DataSourceField,
    aggregationName: string,
    fieldDefinitions: FieldDefinition[],
    variables: ReportVariable[]
): string {
    return mixedOperandHelper.buildAggregatedCaseWithFieldIdentifier(aggregationName, getName(field, fieldDefinitions, variables));
}

function getType(
    field: DataSourceField,
    fieldDefinitions: FieldDefinition[],
    variables: ReportVariable[]
): string {

    if (field.fieldId) {
        return fieldDefinitions.find(f => f.id === field.fieldId)?.type as string;
    } else if (field.variableNumber) {
        return variables.find(v => v.number === field.variableNumber)?.dataType as string;
    } else {
        return '';
    }
}

function getRefId(
    field: DataSourceField,
): string {
    if (field.fieldId) { return `f-${field.fieldId}`; }
    if (field.variableNumber) { return `v-${field.variableNumber}`; }
    return '';
}

const getColumns = (
    onCopyFieldClick: (field: DataSourceField) => void,
    onRemoveFieldClick: (field: DataSourceField) => void,
    isReadOnly: boolean,
    getFieldReferences: (refId: string, aggregationNumber: number) => SelectOption[],
    onFieldSelected: (value: string) => void,
    aggregationNumber: number,
    aggregationName: string,
    reportConfigId: number,
    fieldDefinitions: FieldDefinition[],
    variables: ReportVariable[]
): ColumnDefinition<DataSourceField>[] => {

    let columns: ColumnDefinition<DataSourceField>[] = [
        {
            header: 'Reference',
            getContent: x => getId(x, fieldDefinitions, variables).toString(),
            className: 'break-all',
            width: '110px'
        },
        {
            header: 'Field Name',
            getContent: x => getFullName(x, aggregationName, fieldDefinitions, variables),
        },
        {
            header: 'Data Type',
            getContent: x => getType(x, fieldDefinitions, variables)
        },
        {
            header: '',
            getContent: x => {
                const references = getFieldReferences(getRefId(x), aggregationNumber);
                const refCount = references.length;

                return <div className='display-flex'>
                    <SelectList
                        className="border-none width-130px"
                        options={[{ value: 'References', text: `References (${refCount})` } as SelectOption].concat(references)}
                        value={getName(x, fieldDefinitions, variables)}
                        onChange={(value, event) => { onFieldSelected(value) }}
                    />
                </div>
            },
        },
        {
            header: '',
            getContent: x => {
                const references = getFieldReferences(getRefId(x), aggregationNumber);
                const hasRefs = references.length > 0;

                return <div className="float-right display-flex">
                    <button className="custom-icon-button" onClick={() => {
                        onCopyFieldClick(x)
                    }
                    } disabled={isReadOnly}>
                        <CustomIcon icon={CopyIcon} title={iconTooltip.copy} />
                    </button>
                    {x.id &&
                        <ComponentChangeLogIconAndAction
                            reportConfigId={reportConfigId}
                            id={x.id as number}
                            componentName={getName(x, fieldDefinitions, variables)}
                            componentType={ComponentType.AggregationField} />
                    }
                    <button className="custom-icon-button" onClick={() => {
                        onRemoveFieldClick(x)
                    }
                    } disabled={isReadOnly || hasRefs}>
                        <CustomIcon icon={RemoveIcon} title={iconTooltip.remove} />
                    </button>
                </div>
            },
            width: '40px'
        }
    ];

    return columns;
};

export {
    getColumns
};